/**
 * Type-safe API client for Business Brain memories
 * P1.2: Communicates with /api/brain/memories endpoints
 */

import type { Memory } from "@/lib/constants/memory"

// ============================================================================
// Types
// ============================================================================

export interface ApiError {
  error: string
  message?: string
  status: number
}

export interface MemoriesResponse {
  memories: Memory[]
}

export interface MemoryResponse {
  memory: Memory
}

export interface DeleteResponse {
  success: boolean
}

export interface CreateMemoryInput {
  field: string
  label: string
  value: string
  section: string
  sectionLabel?: string
  category?: string
  categoryLabel?: string
}

export interface UpdateMemoryInput {
  value?: string
  label?: string
  section?: string
  sectionLabel?: string
  category?: string
  categoryLabel?: string
  isArchived?: boolean
}

// ============================================================================
// Request deduplication
// ============================================================================

const inflightRequests = new Map<string, Promise<unknown>>()

function dedupeRequest<T>(key: string, requestFn: () => Promise<T>): Promise<T> {
  const existing = inflightRequests.get(key)
  if (existing) {
    return existing as Promise<T>
  }

  const promise = requestFn().finally(() => {
    inflightRequests.delete(key)
  })

  inflightRequests.set(key, promise)
  return promise
}

// ============================================================================
// API Client
// ============================================================================

class MemoryApiClient {
  private baseUrl = "/api/brain/memories"

  /**
   * Fetch all memories for the authenticated user
   */
  async fetchMemories(): Promise<Memory[]> {
    return dedupeRequest("fetchMemories", async () => {
      const response = await fetch(this.baseUrl, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      })

      if (!response.ok) {
        const error = await this.parseError(response)
        throw error
      }

      const data: MemoriesResponse = await response.json()
      return data.memories
    })
  }

  /**
   * Create a new memory
   */
  async createMemory(input: CreateMemoryInput): Promise<Memory> {
    const response = await fetch(this.baseUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(input),
    })

    if (!response.ok) {
      const error = await this.parseError(response)
      throw error
    }

    const data: MemoryResponse = await response.json()
    return data.memory
  }

  /**
   * Update an existing memory by ID
   */
  async updateMemory(id: string, input: UpdateMemoryInput): Promise<Memory> {
    const response = await fetch(`${this.baseUrl}/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(input),
    })

    if (!response.ok) {
      const error = await this.parseError(response)
      throw error
    }

    const data: MemoryResponse = await response.json()
    return data.memory
  }

  /**
   * Delete a memory by ID
   */
  async deleteMemory(id: string): Promise<boolean> {
    const response = await fetch(`${this.baseUrl}/${id}`, {
      method: "DELETE",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
    })

    if (!response.ok) {
      const error = await this.parseError(response)
      throw error
    }

    const data: DeleteResponse = await response.json()
    return data.success
  }

  /**
   * Parse error response
   */
  private async parseError(response: Response): Promise<ApiError> {
    try {
      const data = await response.json()
      return {
        error: data.error || "Unknown error",
        message: data.message,
        status: response.status,
      }
    } catch {
      return {
        error: "Network error",
        message: response.statusText,
        status: response.status,
      }
    }
  }
}

// Singleton instance
export const memoryApi = new MemoryApiClient()
